import React, {useState} from 'react';

import {createColumnHelper, flexRender, getCoreRowModel, useReactTable} from '@tanstack/react-table';
import {AccountInfoItem} from '../../../../stores/types';
import {formatCurrency, longDateFormatStr} from '../../../../stores/JSHelper';
import Psp from '../../../../components/widget/Psp';
import RefundModal from './RefundModal';

const columnHelper = createColumnHelper<AccountInfoItem>();

function ActiveAccountsTable(props: {activeAccounts: AccountInfoItem[]; onRefundTriggered: () => void}) {
  const {activeAccounts, onRefundTriggered} = props;
  const [refundTarget, setRefundTarget] = useState<AccountInfoItem | null>(null);

  const columns = [
    columnHelper.accessor('accountId', {
      id: 'accountId',
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">Account ID</p>,
      cell: (info) => (
        <div className="flex items-center">
          <p className="ml-3 text-sm font-bold text-navy-700 dark:text-white">{info.getValue()}</p>
        </div>
      ),
    }),
    columnHelper.accessor('externalId', {
      id: 'externalId',
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white">External ID</p>,
      cell: (info) => (
        <div className="flex items-center">
          <p className="ml-3 text-sm font-bold text-navy-700 dark:text-white">{info.getValue()}</p>
        </div>
      ),
    }),
    columnHelper.accessor('name', {
      id: 'name',
      header: () => <p className="text-center text-sm font-bold text-gray-600 dark:text-white">Name</p>,
      cell: (info) => <p className="text-center text-sm font-bold text-navy-700 dark:text-white">
        <div className='flex flex-row items-center justify-start'>
          <div className='mr-2' >
            <Psp empty pspType={info.row.original.pspType} />
          </div>
          {info.getValue()}
        </div>
      </p>,
    }),
    columnHelper.accessor((r) => r.availableWalletBalance.lastUpdatedOn, {
      id: 'updatedOn',
      header: () => <p className="text-center text-sm font-bold text-gray-600 dark:text-white">Updated On</p>,
      cell: (info) => <p className="text-center text-sm font-bold text-navy-700 dark:text-white">{longDateFormatStr(info.getValue())}</p>,
    }),
    columnHelper.accessor((r) => r.availableWalletBalance.balance, {
      id: 'balance',
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white text-center">Balance</p>,
      cell: (info) => (
        <p className="text-right text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() == 0 ? '-' : formatCurrency(info.getValue(), 'INR', false)}
        </p>
      ),
    }),
    columnHelper.accessor((r) => r.availableWalletBalance.availableBalance, {
      id: 'availableBalance',
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white text-center">Available</p>,
      cell: (info) => (
        <p className="text-right text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() == 0 ? '-' : formatCurrency(info.getValue(), 'INR', false)}
        </p>
      ),
    }),
    columnHelper.accessor((r) => r.availableWalletBalance.holdBalance, {
      id: 'holdBalance',
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white text-center">Pending Amount</p>,
      cell: (info) => (
        <p className="text-right text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue() == 0 ? '-' : formatCurrency(info.getValue(), 'INR', false)}
        </p>
      ),
    }),
    columnHelper.accessor((r) => r.availableWalletBalance.holdCount, {
      id: 'holdCount',
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white text-right">Pending</p>,
      cell: (info) => (
        <p className="text-right text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <p className="text-sm font-bold text-gray-600 dark:text-white text-center">Actions</p>,
      cell: (info) => {
        const row = info.row.original;
        const wb = row.availableWalletBalance;
        const hasPending = wb?.holdCount > 0 || wb?.holdBalance > 0;
        const hasBalance = wb?.availableBalance > 0;
        const disabled = !wb || hasPending || !hasBalance;

        let title = 'Refund full available balance';
        if (!wb) title = 'Balance data unavailable';
        else if (hasPending) title = 'Pending transactions — refund blocked';
        else if (!hasBalance) title = 'No available balance to refund';

        return (
          <div className="flex justify-center">
            <button
              disabled={disabled}
              title={title}
              onClick={() => setRefundTarget(row)}
              className={`group flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition duration-200 ${
                disabled
                  ? 'cursor-not-allowed bg-gray-100 text-gray-300 dark:bg-navy-700 dark:text-gray-500'
                  : 'bg-red-50 text-red-600 hover:bg-red-100 active:bg-red-200 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30'
              }`}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
              </svg>
              Refund
            </button>
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: activeAccounts,
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="!border-px !border-gray-400">
              {headerGroup.headers.map((header) => {
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    onClick={() => false}
                    className="cursor-pointer border-b-[1px] border-gray-200 px-6 pb-2 pr-4 pt-4 text-start"
                  >
                    <div className="items-center justify-between text-xs text-gray-200">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: '',
                        desc: '',
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {table.getRowModel().rows.length > 0 && (
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr key={row.id} className="odd:bg-gray-50">
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id} className="min-w-[150px] border-white/0 py-3 pr-6">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        )}
        {table.getRowModel().rows.length == 0 && (
          <tbody>
            <tr>
              <td colSpan={100} className="py-5 text-center">
                No accounts found
              </td>
            </tr>
          </tbody>
        )}
      </table>

      {refundTarget && (
        <RefundModal
          account={refundTarget}
          onClose={() => setRefundTarget(null)}
          onSuccess={() => {
            setRefundTarget(null);
            onRefundTriggered();
          }}
        />
      )}
    </div>
  );
}

export default ActiveAccountsTable;
